<script setup lang="ts">
import {computed} from "vue";
import {storeToRefs} from "pinia";
import {useFilesStore} from "@/stores/files";
import type {Month} from "@/types/files";
import {DynamicByMonthProps} from "@/types/analytics";
import {getDistrictName} from "@/composables/functions/getDistrictName";
import {getRegionName} from "@/composables/functions/getRegionName";

const props = defineProps<DynamicByMonthProps>();

const filesStore = useFilesStore();
const { selectedMonth, selectedYear } = storeToRefs(filesStore);

// Порядок месяцев
const monthsOrder: Month[] = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"
];

const monthNames: Record<string, string> = {
  january: "янв",
  february: "фев",
  march: "мар",
  april: "апр",
  may: "май",
  june: "июн",
  july: "июл",
  august: "авг",
  september: "сен",
  october: "окт",
  november: "ноя",
  december: "дек"
};

// Месяцы для отображения с учётом выбранного года
const monthsToShow = computed(() => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth(); // 0 = январь, 11 = декабрь

  // Если выбран прошлый год — все 12 месяцев
  if (selectedYear.value < currentYear) {
    return monthsOrder;
  }

  // Если текущий год — месяцы до текущего включительно
  return monthsOrder.slice(0, currentMonthIndex + 1);
});

// Данные для отображения
const chartData = computed(() => {
  const dynamicData = props.dynamic;
  if (!dynamicData) return [];

  return monthsToShow.value.map(month => ({
    month,
    value: dynamicData[month] || 0,
    label: monthNames[month]
  }));
});

// Динамическая ширина баров в зависимости от количества месяцев
const barWidth = computed(() => {
  const count = chartData.value.length;
  if (count <= 4) return 42;
  if (count <= 6) return 36;
  if (count <= 8) return 30;
  if (count <= 10) return 26;
  return 22;
});

const maxValue = computed(() => {
  if (chartData.value.length === 0) return 1;
  return Math.max(...chartData.value.map(d => d.value));
});

const minValue = computed(() => {
  if (chartData.value.length === 0) return 0;
  return Math.min(...chartData.value.map(d => d.value));
});

const getBarHeight = (value: number) => {
  if (maxValue.value === minValue.value) return 50;
  const range = maxValue.value - minValue.value;
  const normalizedValue = value - minValue.value;
  // Используем 80% высоты для диапазона, 20% - минимальная высота
  return 20 + (normalizedValue / range) * 80;
};

</script>

<template>
  <div class="dynamic scroll">
    <span class="dynamic__title">
      Динамика по месяцам

      <template v-if="activeType === 'russia'">
        (Россия)
      </template>
      <template v-else-if="activeType === 'district'">
        ({{ getDistrictName(selectedDistrict) }})
      </template>
      <template v-else-if="activeType === 'region'">
        ({{ getRegionName(selectedRegion) }})
      </template>
    </span>

    <div class="dynamic__chart">
      <div
        v-for="item in chartData"
        :key="item.month"
        class="dynamic__block"
      >
        <div
          class="dynamic__bar-container"
          :style="{ width: `${barWidth}px` }"
        >
          <span
            class="dynamic__value"
            :class="{'dynamic__value--active': selectedMonth === item.month}"
          >{{ item.value }}</span>
          <div
            class="dynamic__bar"
            :class="{'dynamic__bar--active': selectedMonth === item.month}"
            :style="{ height: `${getBarHeight(item.value)}%` }"
          />
        </div>
        <span
          class="dynamic__label"
          :class="{'dynamic__label--active': selectedMonth === item.month}"
        >{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dynamic {
  display: flex;
  flex-direction: column;
  gap: 18px;
  background-color: var(--oracul-white-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  padding: 20px 20px 16px 20px;
  border-radius: 8px;

  min-height: 229px;

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }

  &__chart {
    display: flex;
    gap: 10px;
    flex: 1;
    min-height: 0;
  }

  &__block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex: 1;
  }

  &__bar-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    transition: width 0.3s ease;
  }

  &__value {
    font-size: 10px;
    line-height: 10px;
    color: var(--oracul-texticon-gray-text);

    &--active {
      color: var(--oracul-blue-blue100);
    }
  }

  &__bar {
    width: 100%;
    background-color: var(--oracul-blue-blue20);
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;

    &--active {
      background-color: var(--oracul-blue-blue100);
    }
  }

  &__label {
    font-size: 12px;
    line-height: 22px;
    color: var(--oracul-texticon-gray-text);
    height: 22px;

    &--active {
      color: var(--oracul-blue-blue100);
    }
  }
}
</style>